import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthSplitPage } from '../components/AuthSplitPage.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import { authHref, rememberAuthFrom, useAuthNextPath } from '../utils/authRedirect.ts';

function translateResetMessage(t: (key: string) => string, msg: string | undefined): string {
  if (!msg) return t('resetPassword.serverError');
  const keys = [
    'resetPassword.emailRequired',
    'resetPassword.cooldown',
    'resetPassword.emailNotConfigured',
    'resetPassword.emailSendFailed',
    'resetPassword.serverError',
    'resetPassword.sent',
  ];
  return keys.includes(msg) ? t(msg) : msg;
}

export default function ForgotPasswordPage() {
  const { t, currentLanguage } = useTranslation();
  const nextPath = useAuthNextPath();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    rememberAuthFrom(nextPath);
  }, [nextPath]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError(t('resetPassword.emailRequired'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/login/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), lang: currentLanguage }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 429) {
        setError(t('resetPassword.cooldown'));
        return;
      }
      if (!response.ok) {
        setError(translateResetMessage(t, data.message));
        return;
      }
      setSent(true);
      if (typeof data.devResetLink === 'string' && data.devResetLink) {
        setDevLink(data.devResetLink);
      }
    } catch {
      setError(t('resetPassword.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitPage
      title={t('resetPassword.forgotTitle')}
      lead={sent ? t('resetPassword.sent') : t('resetPassword.forgotLead')}
    >
      {error ? <div className="login-card__error">{error}</div> : null}

      {sent ? (
        <>
          {devLink ? (
            <p className="registration-pending-dev">
              {t('registration.devLinkHint')}{' '}
              <a href={devLink}>{devLink}</a>
            </p>
          ) : null}
          <p className="login-card__register">
            <Link to={authHref('/login', nextPath)} className="login-card__register-link">
              {t('resetPassword.backToLogin')}
            </Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          <div className="login-field">
            <label className="login-field__label" htmlFor="forgot-email">
              {t('resetPassword.email')}
            </label>
            <div className="login-field__control">
              <input
                id="forgot-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-field__input"
                placeholder={t('resetPassword.emailPlaceholder')}
                autoComplete="email"
              />
            </div>
          </div>
          <div className="login-submit-wrap">
            <button type="submit" className="login-submit" disabled={loading}>
              <span className="login-submit__label">
                {loading ? t('resetPassword.sending') : t('resetPassword.sendLink')}
              </span>
            </button>
          </div>
          <p className="login-card__register">
            <Link to={authHref('/login', nextPath)} className="login-card__register-link">
              {t('resetPassword.backToLogin')}
            </Link>
          </p>
        </form>
      )}
    </AuthSplitPage>
  );
}
